import { TrustCenterFacadeService } from '@anecdotes/shared-trust-center';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, OnInit } from '@angular/core';
import { filter, firstValueFrom } from 'rxjs';
import { PluginFacadeService, PluginsMetaFacadeService, SlackId } from '@anecdotes/data-store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Service, ServiceInstance, ServiceStatusEnum } from '@anecdotes/api-contracts';
import { WindowHelperService } from '@anecdotes/utils';
import { PluginNavigationService } from '@anecdotes/plugins-connection';
import { DropdownControl } from '@anecdotes/design-system/dropdown-menu';
import { SlackUserChannel } from '../../models/slack-user-channel.model';

@Component({
  selector: 'app-slack-notifications-settings',
  templateUrl: './slack-notifications-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SlackNotificationsSettingsComponent implements OnInit {
  @HostBinding('class')
  private readonly classes = 'flex w-full flex-col gap-4';

  slackEnabled: boolean;
  service: Service;
  instanceDropdownControl = new DropdownControl({});
  channelsUsersData: SlackUserChannel[];
  selectedChannelsUsers: string[];
  selectedInstance: string;

  get isInstalled(): boolean {
    return this.service?.service_status === ServiceStatusEnum.INSTALLED;
  }

  get hasMultipleInstances(): boolean {
    return this.installedInstances?.length > 1;
  }

  get installedInstances(): ServiceInstance[] {
    return this.service?.service_instances_list?.filter(
      (instance) => instance.service_status === ServiceStatusEnum.INSTALLED,
    );
  }

  constructor(
    private windowHelper: WindowHelperService,
    private trustCenterFacadeService: TrustCenterFacadeService,
    private pluginFacadeService: PluginFacadeService,
    private pluginsNavigationService: PluginNavigationService,
    private pluginsMetaFacade: PluginsMetaFacadeService,
    private cd: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.service = await firstValueFrom(this.pluginFacadeService.getServiceById(SlackId).pipe(filter(Boolean)));
    this.slackEnabled = await firstValueFrom(this.trustCenterFacadeService.getSlackNotifications());

    this.trustCenterFacadeService
      .getSlackRecipients()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((channelsUsersIds: string[]) => {
        this.selectedChannelsUsers = channelsUsersIds;
        this.cd.detectChanges();
      });

    await this.initData();

    this.instanceDropdownControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((instance: ServiceInstance) => {
        this.selectedChannelsUsers = [];
        this.loadChannels(instance);
      });
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  openHowToConnectPage(): void {
    this.windowHelper.openUrlInNewTab(this.service.service_intercom_article);
  }

  openPluginPage(): void {
    this.pluginsNavigationService.navigateToPluginDetailsInNewTab(this.service.service_id);
  }

  displayAccountValueSelector(item: ServiceInstance): string {
    return item.service_instance_display_name;
  }

  async updateSlackEnabledFlag(slackEnabled: boolean): Promise<void> {
    const previousValue = this.slackEnabled;
    this.slackEnabled = slackEnabled;
    this.cd.detectChanges();

    try {
      if (slackEnabled) {
        await this.initData();
      }
      await this.trustCenterFacadeService.updateSettings({
        slack_notification: slackEnabled,
        slack_account: this.instanceDropdownControl.value.service_instance_id,
      });
    } catch {
      this.slackEnabled = previousValue;
      this.cd.detectChanges();
    }
  }

  private async initData(): Promise<void> {
    this.selectedInstance = await firstValueFrom(this.trustCenterFacadeService.getSlackAccount());

    const instance: ServiceInstance =
      (this.selectedInstance &&
        this.installedInstances?.find((instnace) => instnace.service_instance_id === this.selectedInstance)) ||
      this.installedInstances?.[0];

    this.instanceDropdownControl.setValue(instance, { emitEvent: false });
    this.loadChannels(instance);

    this.cd.detectChanges();
  }

  private async loadChannels(instance: ServiceInstance): Promise<void> {
    const slackMetadata = await firstValueFrom(
      this.pluginsMetaFacade.getServiceMetadata(instance.service_id, instance.service_instance_id),
    );

    this.channelsUsersData = slackMetadata.channels?.concat(
      slackMetadata.users?.map((user) => {
        return { id: user.id, name: user.real_name };
      }) || [],
    );

    this.cd.detectChanges();
  }
}
